import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Layout, Row, Col, Button, Checkbox, message, Input } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { getStateSliceProp, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import UserNotificationRowDisplay from '../user_notification_row_display/UserNotificationRowDisplay';
import { GLOBAL_DEBOUNCE_TIME, ROLE_TYPES } from '../../utils/DataConstants';
import {
	markAllUserNotificationsRead,
	markSelectedUserNotificationsRead,
	markSelectedUserNotificationsUnread,
	parseURL,
	userNotificationsRestCrudThunksForBuyer,
	userNotificationsRestCrudThunksForSupplier,
} from '../../thunks/user_notifications_thunks';
import { USER_NOTIFICATIONS_CRUD_ACTION_CREATORS } from '../../actions/user_notifications_actions';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { debounce } from '../../utils/PerformanceUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';

const { Content } = Layout;

require('./NotificationsIndexPage.less');

const NOTIFICATIONS_OVERVIEW_TARGET_COLLECTION = 'notificationsIndex';

const NotificationsIndexPage: FC<any> = ({
	userNotifications,
	userType,
	showFilters = true,
	showUnreadFilter = true,
	showSearch = true,
	emptyState,
	preAppliedFilters = {},
	typeFilters = [],
	targetCollectionName,
	onNotificationClick,
	extraFilters,
	history,
	location,
	markAllRead,
	markSelectedRead,
	markSelectedUnread,
	fetchUserNotifications,
	updateUserNotificationFilters,
}): React.ReactElement => {
	const [isNoTypeFilter, setIsNoTypeFilter] = React.useState(false);
	const [markinAllReadLoading, setMarkinAllReadLoading] = React.useState(false);
	const [latestUnreadNotificationId, setLatestUnreadNotificationId] = React.useState(null);
	const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
	const [selectionTypeSeen, setselectionTypeSeen] = React.useState(false);

	const debouncedUpdateUserNotificationFilters = React.useCallback(
		debounce((filters, targetCollection) => {
			updateUserNotificationFilters(filters, targetCollection);
		}, GLOBAL_DEBOUNCE_TIME),
		[]
	);

	const targetCollection = targetCollectionName || NOTIFICATIONS_OVERVIEW_TARGET_COLLECTION;
	const activeFilters = nullSafeGetOrElse(`${targetCollection}.filters`, userNotifications, {});

	const refreshNotifications = useCallback(() => {
		fetchUserNotifications(
			{},
			targetCollection,
			getStateSliceProp('pagination', targetCollection, userNotifications),
			getStateSliceProp('sorting', targetCollection, userNotifications),
			getStateSliceProp('filters', targetCollection, userNotifications)
		);
	}, [fetchUserNotifications, targetCollection, userNotifications]);

	React.useEffect(() => {
		const notifications = getRecordsForTargetCollection(userNotifications, targetCollection);
		if (
			nullSafeGet(`${targetCollection}.pagination.current`, userNotifications) === 1 &&
			notifications.length > 0
		) {
			const latestUnreadNotification = notifications[0];
			setLatestUnreadNotificationId(latestUnreadNotification.id);
		}
	}, [userNotifications, targetCollection]);

	const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows) => {
		setselectionTypeSeen(selectedRows.length > 0 && selectedRows[0].hasSeen);
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const prepareIntialFilters = React.useCallback(() => {
		const allFilters = typeFilters
			.map((filter) => {
				if (filter.children && filter.children.length > 0) {
					return filter.children.map((child) => child.key);
				}
				return filter.key;
			})
			.flat();
		const initialFilters = { ...preAppliedFilters };
		if (allFilters.length > 0) {
			initialFilters.notificationTypes = allFilters;
		}
		const { filters } = parseURL(true, history, location, {}, {}, {}, initialFilters);
		// this is to fix the issue where the filters are coming as string instead of array, as our parseURL/getBackLinkToParams is not handling it properly.
		if (filters.notificationTypes && typeof filters.notificationTypes === 'string') {
			filters.notificationTypes = filters.notificationTypes.split(',');
		}
		return filters;
	}, [typeFilters, preAppliedFilters, history, location]);

	const handleMarkAllRead = React.useCallback(() => {
		const unreadNotifications = getRecordsForTargetCollection(userNotifications, targetCollection);

		if (unreadNotifications && unreadNotifications.length > 0) {
			const latestUnreadNotification = unreadNotifications[0];
			setMarkinAllReadLoading(true);
			markAllRead({
				id: latestUnreadNotificationId || latestUnreadNotification.id,
				...activeFilters,
				hasSeen: undefined,
			})
				.then(() => {
					onNotificationClick && onNotificationClick();
				})
				.catch((error) => {
					message.error('Failed to mark all notifications as read');
				})
				.finally(() => {
					setMarkinAllReadLoading(false);
				});
		}
	}, [
		userNotifications,
		targetCollection,
		latestUnreadNotificationId,
		activeFilters,
		onNotificationClick,
		markAllRead,
	]);

	const handleMarkSelectedRead = React.useCallback(() => {
		const notificationIds = selectedRowKeys;

		if (notificationIds && notificationIds.length > 0) {
			const action = selectionTypeSeen ? markSelectedUnread : markSelectedRead;
			setMarkinAllReadLoading(true);
			action(notificationIds)
				.finally(() => {
					setMarkinAllReadLoading(false);
				})
				.then(() => {
					setSelectedRowKeys([]);
					onNotificationClick && onNotificationClick();
				})
				.catch((error) => {
					message.error('Failed to mark selected notifications as read');
				});
		}
	}, [
		selectedRowKeys,
		markSelectedRead,
		onNotificationClick,
		markSelectedUnread,
		selectionTypeSeen,
	]);

	const selectedFilters = activeFilters.notificationTypes || [];
	const notificationsFetching = userNotifications.fetching;

	const formatNotifications = React.useCallback(
		(notification) => ({
			...notification,
			title:
				userType === ROLE_TYPES.SUPPLIER
					? nullSafeGet(`supplierPushNotification.webAppMessage`, notification)
					: nullSafeGet(`buyerPushNotification.webAppMessage`, notification),
			datetime: notification.createdAt,
			contact: notification.userContact,
		}),
		[userType]
	);

	const rowSelection = React.useMemo(
		() => ({
			selectedRowKeys,
			onChange: onSelectChange,
			getCheckboxProps: (record) => {
				if (selectedRowKeys.length === 0) {
					return {};
				}
				return {
					disabled: selectionTypeSeen ? !record.hasSeen : record.hasSeen,
				};
			},
			preserveSelectedRowKeys: true,
		}),
		[selectedRowKeys, selectionTypeSeen]
	);

	return (
		<Content
			className="buyerNotificationsIndexPage"
			style={{ padding: '0 0.5em' }}
			key={targetCollectionName}
		>
			<Row style={{ margin: '0.5em -8px' }}>
				<Col span={24}>
					<div className="mx-2 mb-4 mt-4  flex flex-row flex-wrap  items-center justify-between">
						<div className=" flex basis-1/2 gap-x-4 gap-y-2 ">
							{showSearch && (
								<Input.Search
									placeholder={'Search by WO#'}
									value={activeFilters.search}
									style={{ width: 180 }}
									onChange={(e) => {
										const value =
											nullSafeGetOrElse('target.value.length', e, 0) > 0
												? e.target.value
												: undefined;
										debouncedUpdateUserNotificationFilters({ search: value }, targetCollection);
									}}
								/>
							)}
							{showUnreadFilter && (
								<OWRadioGroup
									items={[
										{ label: 'All', value: undefined },
										{ label: 'Unread', value: false },
									]}
									value={activeFilters.hasSeen}
									valueAccessor={(item) => item.value}
									onChange={(hasSeen) => {
										debouncedUpdateUserNotificationFilters({ hasSeen: hasSeen }, targetCollection);
									}}
									renderRecord={(item) => item.label}
								/>
							)}
							{(showFilters && [
								<OWRadioGroup
									items={[
										{ label: 'All', value: undefined },
										{ label: 'Emergency', value: true },
									]}
									value={activeFilters.isEmergency}
									valueAccessor={(item) => item.value}
									onChange={(isEmergency) => {
										debouncedUpdateUserNotificationFilters(
											{ isEmergency: isEmergency },
											targetCollection
										);
									}}
									renderRecord={(item) => item.label}
								/>,
							]) ||
								null}

							{extraFilters &&
								extraFilters({
									updateFilters: debouncedUpdateUserNotificationFilters,
									targetCollection,
									activeFilters,
								})}
						</div>
						{(typeFilters.length && (
							<div className="flex basis-1/2 flex-row flex-wrap justify-end gap-x-4 gap-y-2">
								{typeFilters.map((filter) => (
									<Checkbox
										onChange={(e) => {
											let updatedFilters = [];
											if (e.target.checked) {
												if (filter.children && filter.children.length > 0) {
													updatedFilters = [
														...selectedFilters,
														...filter.children.map((child) => child.key),
													];
												} else {
													updatedFilters = [...selectedFilters, filter.key];
												}
											} else {
												if (filter.children && filter.children.length > 0) {
													updatedFilters = selectedFilters.filter(
														(selectedFilter) =>
															!filter.children.map((child) => child.key).includes(selectedFilter)
													);
												} else {
													updatedFilters = selectedFilters.filter(
														(selectedFilter) => selectedFilter !== filter.key
													);
												}
											}
											if (updatedFilters.length === 0) {
												setIsNoTypeFilter(true);
											} else {
												setIsNoTypeFilter(false);
											}
											debouncedUpdateUserNotificationFilters(
												{ notificationTypes: updatedFilters },
												targetCollection
											);
										}}
										checked={(
											(filter.children &&
												filter.children.length &&
												filter.children.map((child) => child.key)) || [filter.key] ||
											[]
										).every((val) => selectedFilters.includes(val))}
										key={filter.key}
									>
										{filter.label}
									</Checkbox>
								))}
							</div>
						)) ||
							null}
						<div className="mx-2 flex grow  flex-row flex-wrap items-end justify-end">
							{(selectedRowKeys.length && (
								<Button onClick={handleMarkSelectedRead} type="ghost" className="mr-2">
									Mark selected ({selectedRowKeys.length}) {selectionTypeSeen ? 'unread' : 'read'}
								</Button>
							)) ||
								null}
							{showUnreadFilter && (
								<Button onClick={handleMarkAllRead} type="ghost">
									Mark all read
								</Button>
							)}
						</div>
					</div>
				</Col>
				<Col span={24}>
					{(markinAllReadLoading && <PageLoadingPlaceholder />) || (
						<>
							{(isNoTypeFilter && (
								<EmptyState
									graphic={
										<img
											style={{ marginBottom: 8 }}
											src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
											alt="404"
										/>
									}
									headline={'Please select a notification type to view notifications'}
									body={null}
								/>
							)) || (
								<PaginatedReduxTable
									keyAccessor={(notification) => notification.id}
									updateQueryParams
									loading={notificationsFetching}
									targetCollectionName={targetCollection}
									showHeader={false}
									columns={[
										{
											title: '',
											dataIndex: 'id',
											key: 'id',
											render: (title, notification) => (
												<UserNotificationRowDisplay
													showTemplate
													notification={formatNotifications(notification)}
													userType={userType}
													onUpdate={onNotificationClick}
													onRefresh={refreshNotifications}
												/>
											),
										},
									]}
									collectionName={'user_notifications'}
									fetchData={
										userType === ROLE_TYPES.SUPPLIER
											? userNotificationsRestCrudThunksForSupplier.read
											: userNotificationsRestCrudThunksForBuyer.read
									}
									emptyState={
										emptyState ? (
											emptyState
										) : (
											<EmptyState
												graphic={
													<img
														style={{ marginBottom: 8 }}
														src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
														alt="404"
													/>
												}
												headline={'No Notifications Found'}
												body={null}
											/>
										)
									}
									initialFilters={prepareIntialFilters()}
									rowSelection={rowSelection}
								/>
							)}
						</>
					)}
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	userNotifications: state.user_notifications,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateUserNotificationFilters: (filters, targetCollection) =>
		dispatch(USER_NOTIFICATIONS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	markAllRead: (entity) => dispatch(markAllUserNotificationsRead(ownProps.userType)(entity)),
	markSelectedRead: (ids) => dispatch(markSelectedUserNotificationsRead(ownProps.userType)(ids)),
	markSelectedUnread: (ids) =>
		dispatch(markSelectedUserNotificationsUnread(ownProps.userType)(ids)),
	fetchUserNotifications: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? userNotificationsRestCrudThunksForSupplier
				: userNotificationsRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(NotificationsIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
